/**
 * Normalizes Discogs URLs by stripping locale prefixes and standardizing format
 * 
 * Examples:
 * - https://www.discogs.com/en/release/123 -> discogs.com/release/123
 * - https://www.discogs.com/fr/release/123 -> discogs.com/release/123
 * - https://discogs.com/de/master/456 -> discogs.com/master/456
 * - http://www.discogs.com/release/789 -> discogs.com/release/789
 * 
 * @param {string} url - The Discogs URL to normalize
 * @returns {string} - Normalized URL in format: discogs.com/{type}/{id}
 */
export const normalizeDiscogsUrl = (url) => {
    if (!url || typeof url !== 'string') {
        return url;
    }

    // Trim whitespace
    let normalized = url.trim();

    // Add protocol if missing (needed for URL parsing)
    if (!normalized.startsWith('http://') && !normalized.startsWith('https://')) {
        normalized = 'https://' + normalized;
    }

    try {
        const parsedUrl = new URL(normalized);

        // Check if this is actually a Discogs URL
        // Hostname must be exactly 'discogs.com' or end with '.discogs.com' (for subdomains)
        // This prevents false matches like 'discogs.com.fake.com' or 'fake-discogs.com'
        if (!parsedUrl.hostname ||
            (parsedUrl.hostname !== 'discogs.com' &&
                !parsedUrl.hostname.endsWith('.discogs.com'))) {
            return url.trim();
        }

        // Extract pathname and split into parts
        const pathParts = parsedUrl.pathname.split('/').filter(part => part.length > 0);

        // Common 2-letter locale codes used by Discogs
        // ISO 639-1 language codes (most common ones)
        const localeCodes = new Set([
            'en', 'fr', 'de', 'es', 'it', 'pt', 'ru', 'ja', 'ko', 'zh', 'nl', 'pl', 'tr', 'sv', 'da',
            'fi', 'no', 'cs', 'hu', 'ro', 'el', 'th', 'vi', 'id', 'he', 'ar', 'hi', 'uk', 'bg', 'hr',
            'sk', 'sl', 'et', 'lv', 'lt', 'mt', 'ga', 'cy', 'is', 'mk', 'sq', 'sr', 'bs', 'ka', 'hy',
            'az', 'be', 'kk', 'ky', 'uz', 'mn', 'ne', 'si', 'my', 'km', 'lo', 'ka', 'am', 'sw', 'zu',
            'af', 'eu', 'ca', 'gl', 'br', 'gd', 'co', 'lb', 'fy', 'yi', 'jv', 'su', 'ceb', 'haw', 'mg'
        ]);

        // Check if first path segment is a locale code
        let pathStartIndex = 0;
        if (pathParts.length > 0 && localeCodes.has(pathParts[0].toLowerCase())) {
            // First segment is a locale code, skip it
            pathStartIndex = 1;
        }

        // Reconstruct path without locale
        const pathWithoutLocale = '/' + pathParts.slice(pathStartIndex).join('/');

        // Return normalized format: discogs.com/{type}/{id}
        return `discogs.com${pathWithoutLocale}`;
    } catch (error) {
        // If URL parsing fails, return original (trimmed)
        return url.trim();
    }
};

/**
 * Extracts release ID from a Discogs URL
 * Handles both release and master URLs, with or without locale prefixes
 * 
 * @param {string} url - The Discogs URL
 * @returns {string|null} - The release ID, or null if not found
 */
export const extractReleaseId = (url) => {
    const normalized = normalizeDiscogsUrl(url);
    const match = normalized.match(/discogs\.com\/(?:release|master)\/([^/-]+)/);
    return match ? match[1] : null;
};

/**
 * Checks if URL is a master release
 * 
 * @param {string} url - The Discogs URL
 * @returns {boolean} - True if URL is a master release
 */
export const isMasterRelease = (url) => {
    const normalized = normalizeDiscogsUrl(url);
    return normalized.includes('/master/');
};
